import './SegmentedControlButton.scss';
import cn from 'classnames';
import { ReactElement } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import { Button } from '../../Button/Button';
import { Background, Design, Size } from '../../designConstants';
import {
  BaseSegmentedControlButtonProps,
  ExclusiveSelectModeProps,
  MultiSelectModeProps
} from '../segmented-control-types';
import { getButtonDesign, getIsActive } from '../segmented-control-utils';

interface Option<T> {
  key: T;
  label?: string;
  renderLabel?: (label: string) => ReactElement;
  tooltip?: string;
  /** Greys out just this option (the whole control has its own `disabled`). */
  disabled?: boolean;
}

interface BaseProps<T> extends BaseSegmentedControlButtonProps {
  options: readonly (Option<T> | T)[];
}

interface ExclusiveSelectProps<T extends string>
  extends BaseProps<T>, ExclusiveSelectModeProps<T> {}
interface MultiSelectProps<T extends string> extends BaseProps<T>, MultiSelectModeProps<T> {}

type SegmentedControlButtonProps<T extends string> = ExclusiveSelectProps<T> | MultiSelectProps<T>;

export const SegmentedControlButton = <T extends string>({
  className,
  design = Design.BRAND,
  background = Background.LIGHT,
  size = Size.MD,
  disabled = false,
  tooltipClass,
  tooltipPlacement,
  options,
  ...modeProps
}: SegmentedControlButtonProps<T>): ReactElement => {
  const isActive = getIsActive(modeProps);

  return (
    <ButtonGroup
      className={cn('segmented-control', design, background, size, className, {
        disabled,
        enabled: !disabled
      })}
    >
      {options.map(option => {
        const {
          key,
          label,
          renderLabel,
          tooltip,
          disabled: optionDisabled = false
        } = typeof option === 'string'
          ? {
              key: option,
              label: option,
              renderLabel: (v: string) => v
            }
          : {
              key: option.key,
              label: option.label ?? option.key,
              renderLabel: option.renderLabel ?? ((v: string) => v),
              tooltip: option.tooltip
                ? { text: option.tooltip, placement: tooltipPlacement, class: tooltipClass }
                : undefined,
              disabled: option.disabled
            };
        const active = isActive(key);
        const buttonDesign = getButtonDesign(background, active, design);

        return (
          <Button
            {...buttonDesign}
            size={size}
            key={key}
            className={cn('segmented-item', background, {
              selected: active,
              unselected: !active
            })}
            disabled={
              (modeProps.isExclusiveSelect !== false && active) || disabled || optionDisabled
            }
            onClick={() => modeProps.onChange(key)}
            tooltip={tooltip}
          >
            {renderLabel(label)}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};
